import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  FileSpreadsheet,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNumber } from "@/lib/format";
import { useNavigationLock } from "@/hooks/useNavigationLock";
import ListFilterBar from "@/components/ListFilterBar";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import { useClearFilterParams, useFilterParam } from "@/hooks/useFilterParam";

// Excel imports are chunked client-side so each request stays well under the
// Cloudflare Workers subrequest budget (paid plan: 1000 — each row does ~2 D1
// calls, so 50 rows ≈ 100 subrequests, comfortably below the limit) and so we
// can show real "row X of N" progress to the user.
const IMPORT_CHUNK_SIZE = 50;
const PRODUCT_LIST_PAGE_SIZE = 1000;
const countFormatter = new Intl.NumberFormat();

interface Product {
  id: number;
  sku: string | null;
  name: string;
  description: string | null;
  unit: string;
  unit_price: number;
  tax_rate: number;
  isActive?: boolean;
}

interface ImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  errors?: { row: number; error: string }[];
}

interface ProductListResponse {
  products: Product[];
  total?: number;
  limit?: number;
  offset?: number;
}

const empty: Partial<Product> = {
  sku: "",
  name: "",
  description: "",
  unit: "Pieces",
  unit_price: 0,
  tax_rate: 0,
};

const TEMPLATE_HEADERS = ["Name", "Unit", "DefaultRate"] as const;

function pickField(
  row: Record<string, unknown>,
  ...keys: string[]
): unknown {
  for (const k of keys) {
    if (k in row) return row[k];
    const lower = k.toLowerCase();
    for (const rk of Object.keys(row)) {
      if (rk.toLowerCase() === lower) return row[rk];
    }
  }
  return undefined;
}

export default function Products() {
  const { can, isFeatureEnabled } = useAuth();
  const writable = can("products.write");
  // The existing products.delete permission is repurposed as the
  // "can disable / enable" gate (the trash action became a non-destructive
  // Disable/Enable toggle).
  const canToggleActive = can("products.delete");
  // Product-level default Tax % is only meaningful when the Tax feature is ON;
  // hide the input + table column otherwise (mirrors the global tax gating).
  const taxEnabled = isFeatureEnabled("tax");
  // Columns rendered in the product table = 8 normally, 7 when the Tax % column
  // is hidden (the new Status column adds one to the prior 7/6). Keep the
  // empty/loading-state colSpan in lockstep.
  const productColCount = taxEnabled ? 8 : 7;
  const canManageAttributes = can("inventory.attributes.manage");

  const [rows, setRows] = useState<Product[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [search, setSearch] = useFilterParam("q");
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const clearFilters = useClearFilterParams(["q"]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Product> | null>(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useNavigationLock(
    importing,
    "An Excel import is still running. Leave anyway? Unfinished rows will not be imported.",
  );

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return rows;
    return rows.filter(
      (p) =>
        p.name.toLowerCase().includes(s) ||
        (p.sku ?? "").toLowerCase().includes(s),
    );
  }, [rows, search]);

  const pg = usePagination(filtered);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function load() {
    setLoading(true);
    try {
      const all: Product[] = [];
      let offset = 0;
      let total = 0;

      while (true) {
        const params = new URLSearchParams({
          limit: String(PRODUCT_LIST_PAGE_SIZE),
          offset: String(offset),
        });
        // Inactive items are hidden by default; the "Show inactive" toggle
        // opts into them via the API's includeInactive param.
        if (showInactive) params.set("includeInactive", "1");
        const res = await api.get<ProductListResponse>(
          `/products?${params.toString()}`,
        );
        const page = res.products ?? [];
        all.push(...page);
        total = Number(res.total ?? all.length);

        if (page.length < PRODUCT_LIST_PAGE_SIZE || all.length >= total) break;
        offset += page.length;
      }

      setRows(all.map((p) => ({ ...p, isActive: p.isActive !== false })));
      setTotalProducts(Math.max(total, all.length));
    } catch (err) {
      toast.error((err as Error).message || "Failed to load inventory");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const openNew = () => {
    setEditing({ ...empty });
    setOpen(true);
  };

  const openEdit = (p: Product) => {
    setEditing(p);
    setOpen(true);
  };

  async function save() {
    if (!editing) return;
    if (!String(editing.name || "").trim())
      return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        ...editing,
        unit_price: Number(editing.unit_price ?? 0),
        tax_rate: Number(editing.tax_rate ?? 0),
      };
      if (editing.id) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Inventory item updated");
      } else {
        await api.post("/products", payload);
        toast.success("Inventory item created");
      }
      setOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error((err as Error).message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  // Non-destructive disable / enable. Disabling routes through DELETE (which
  // the API now soft-disables); enabling sends is_active back to 1 via PUT.
  // Both are gated by the products.delete permission in the UI.
  async function toggleActive(p: Product) {
    const nextActive = p.isActive === false;
    if (!nextActive && !confirm(`Disable "${p.name}"?`)) return;
    setRows((prev) =>
      prev.map((r) => (r.id === p.id ? { ...r, isActive: nextActive } : r)),
    );
    try {
      if (nextActive) {
        await api.put(`/products/${p.id}`, { is_active: 1 });
        toast.success("Inventory item enabled");
      } else {
        await api.del(`/products/${p.id}`);
        toast.success("Inventory item disabled");
      }
      await load();
    } catch (err) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === p.id ? { ...r, isActive: p.isActive } : r,
        ),
      );
      toast.error((err as Error).message || "Update failed");
    }
  }

  function downloadTemplate() {
    const ws = XLSX.utils.aoa_to_sheet([
      [...TEMPLATE_HEADERS],
      ["Standard Shingle", "Pieces", 25],
      ["Underlayment Roll", "Roll", 80],
    ]);
    ws["!cols"] = [{ wch: 28 }, { wch: 12 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.writeFile(wb, "products-template.xlsx");
    toast.success("Template downloaded");
  }

  function exportExcel() {
    if (!rows.length) {
      toast.error("No inventory items to export");
      return;
    }
    const data = rows.map((p) => ({
      Name: p.name,
      Unit: p.unit || "Pieces",
      DefaultRate: Number(p.unit_price || 0),
    }));
    const ws = XLSX.utils.json_to_sheet(data, { header: [...TEMPLATE_HEADERS] });
    ws["!cols"] = [{ wch: 28 }, { wch: 12 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `products-${stamp}.xlsx`);
    toast.success(
      `Exported ${rows.length} inventory item${rows.length === 1 ? "" : "s"}`,
    );
  }

  function triggerImport() {
    fileInputRef.current?.click();
  }

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const MAX_IMPORT_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_IMPORT_BYTES) {
      toast.error("Spreadsheet must be 5 MB or smaller.");
      e.target.value = "";
      return;
    }
    setImporting(true);
    setImportProgress(null);
    const toastId = "products-import";
    toast.loading("Reading spreadsheet…", { id: toastId });
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames[0];
      if (!sheetName) throw new Error("No sheet found in file");
      const sheet = wb.Sheets[sheetName];
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
        defval: "",
      });
      const products = raw
        .map((row) => {
          const name = String(pickField(row, "Name", "name") ?? "").trim();
          if (!name) return null;
          const unitRaw = pickField(row, "Unit", "unit");
          const unit = String(unitRaw ?? "").trim() || "Pieces";
          const rateRaw = pickField(
            row,
            "DefaultRate",
            "Default Rate",
            "Rate",
            "default_rate",
            "unit_price",
            "Unit Price",
            "Price",
          );
          const unit_price = Number(rateRaw ?? 0) || 0;
          const sku = String(pickField(row, "SKU", "sku") ?? "").trim();
          const description = String(
            pickField(row, "Description", "description") ?? "",
          );
          return {
            name,
            unit,
            unit_price,
            sku: sku || undefined,
            description: description || undefined,
          };
        })
        .filter(Boolean) as Array<Record<string, unknown>>;

      if (!products.length) {
        toast.error("No valid rows found in file", { id: toastId });
        return;
      }

      const total = products.length;
      setImportProgress({ done: 0, total });

      // Chunk the upload so each request stays under the Worker subrequest
      // budget. The previous single-shot POST silently dropped rows once the
      // server's per-row D1 calls exhausted that budget — that's why large
      // sheets came back "cut off". Each batch is a self-contained call to
      // the existing /products/import endpoint, so the API contract is
      // unchanged (server already loops the array it receives).
      let inserted = 0;
      let updated = 0;
      let skipped = 0;
      const errors: { row: number; error: string }[] = [];
      const failedBatches: number[] = [];

      for (let offset = 0; offset < total; offset += IMPORT_CHUNK_SIZE) {
        const slice = products.slice(offset, offset + IMPORT_CHUNK_SIZE);
        const upTo = Math.min(offset + slice.length, total);
        toast.loading(`Importing row ${offset + 1}–${upTo} of ${total}…`, {
          id: toastId,
        });
        try {
          const res = await api.post<ImportResult>("/products/import", {
            products: slice,
          });
          inserted += res.inserted;
          updated += res.updated;
          skipped += res.skipped;
          if (res.errors?.length) {
            for (const er of res.errors) {
              errors.push({ row: offset + er.row, error: er.error });
            }
          }
        } catch (err) {
          // A single batch failed — record it and keep going so the rest of
          // the file still imports.
          const msg = err instanceof Error ? err.message : "batch failed";
          failedBatches.push(offset + 1);
          for (let i = 0; i < slice.length; i++) {
            errors.push({ row: offset + i + 1, error: msg });
          }
        }
        setImportProgress({ done: upTo, total });
      }

      const ok = inserted + updated;
      toast.success(
        `Imported ${ok} of ${total} (new ${inserted}, updated ${updated})` +
          (skipped ? `, skipped ${skipped}` : "") +
          (errors.length ? `, ${errors.length} failed` : ""),
        { id: toastId },
      );
      if (failedBatches.length) {
        toast.error(
          `${failedBatches.length} batch(es) failed to upload — check your connection and retry.`,
        );
      }
      await load();
    } catch (err) {
      toast.error((err as Error).message || "Import failed", { id: toastId });
    } finally {
      setImporting(false);
      setImportProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">Inventory</h1>
            <Badge variant="secondary">
              {countFormatter.format(totalProducts)} total - showing{" "}
              {countFormatter.format(search.trim() ? filtered.length : rows.length)}
            </Badge>
          </div>
          <p className="text-muted-foreground">
            Manage your inventory catalogue and import/export with Excel.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none mr-1">
            <Checkbox
              checked={showInactive}
              onCheckedChange={(v) => setShowInactive(v === true)}
            />
            Show inactive
          </label>
          {writable ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={handleImportFile}
                disabled={importing}
              />
              <Button
                variant="outline"
                onClick={triggerImport}
                disabled={importing}
                title="Import inventory from an Excel or CSV file"
              >
                <Upload className="h-4 w-4 mr-2" />
                {importing
                  ? importProgress
                    ? `Importing ${importProgress.done}/${importProgress.total}…`
                    : "Importing…"
                  : "Import Excel"}
              </Button>
              <Button
                variant="outline"
                onClick={downloadTemplate}
                disabled={importing}
                title="Download a blank Excel template"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </>
          ) : null}
          <Button
            variant="outline"
            onClick={exportExcel}
            disabled={importing}
            title="Export current inventory list to Excel"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
          {canManageAttributes ? (
            <Button variant="outline" asChild>
              <Link to="/inventory/attributes?tab=gauges">Manage Attributes</Link>
            </Button>
          ) : null}
          {writable ? (
            <Dialog
              open={open}
              onOpenChange={(next) => {
                if (importing) return;
                setOpen(next);
              }}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={openNew}
                  className="bg-gradient-primary"
                  disabled={importing}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Inventory Item
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editing?.id ? "Edit Inventory Item" : "New Inventory Item"}
                  </DialogTitle>
                </DialogHeader>
                <div className="grid gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>SKU</Label>
                      <Input
                        value={editing?.sku ?? ""}
                        onChange={(e) =>
                          setEditing((f) => ({
                            ...(f ?? {}),
                            sku: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Name *</Label>
                      <Input
                        value={editing?.name ?? ""}
                        onChange={(e) =>
                          setEditing((f) => ({
                            ...(f ?? {}),
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label>Unit</Label>
                      <Input
                        value={editing?.unit ?? "Pieces"}
                        onChange={(e) =>
                          setEditing((f) => ({
                            ...(f ?? {}),
                            unit: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Unit price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={String(editing?.unit_price ?? "")}
                        onChange={(e) =>
                          setEditing((f) => ({
                            ...(f ?? {}),
                            unit_price: Number(e.target.value),
                          }))
                        }
                      />
                    </div>
                    {taxEnabled ? (
                      <div className="space-y-1.5">
                        <Label>Tax %</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={String(editing?.tax_rate ?? "")}
                          onChange={(e) =>
                            setEditing((f) => ({
                              ...(f ?? {}),
                              tax_rate: Number(e.target.value),
                            }))
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Description</Label>
                    <Textarea
                      rows={3}
                      value={editing?.description ?? ""}
                      onChange={(e) =>
                        setEditing((f) => ({
                          ...(f ?? {}),
                          description: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setOpen(false)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={save}
                    className="bg-gradient-primary"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
        <ListFilterBar
          title="Search Inventory"
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search inventory..."
          hasAnyFilter={Boolean(search)}
          onClear={clearFilters}
        />
        <Card className="shadow-card">
          <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Default Rate</TableHead>
              {taxEnabled ? (
                <TableHead className="text-right">Tax %</TableHead>
              ) : null}
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={productColCount}
                  className="text-center text-muted-foreground py-10"
                >
                  Loading...
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={productColCount}
                  className="text-center text-muted-foreground py-10"
                >
                  No inventory items.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              pg.pageItems.map((p, i) => (
                <TableRow key={p.id}>
                  <TableCell className="text-muted-foreground">
                    {pg.from + i}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {p.sku || "—"}
                  </TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.unit}</TableCell>
                  <TableCell className="text-right font-mono">
                    {formatNumber(Number(p.unit_price || 0))}
                  </TableCell>
                  {taxEnabled ? (
                    <TableCell className="text-right font-mono">
                      {formatNumber(Number(p.tax_rate || 0))}
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        p.isActive !== false
                          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                          : "border-muted-foreground/30 bg-muted text-muted-foreground"
                      }
                    >
                      {p.isActive !== false ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1 justify-end">
                      {writable ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(p)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4 text-primary" />
                        </Button>
                      ) : null}
                      {canToggleActive ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => toggleActive(p)}
                          title={p.isActive !== false ? "Disable" : "Enable"}
                        >
                          {p.isActive !== false ? (
                            <PowerOff className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                          ) : (
                            <Power className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && filtered.length > 0 ? (
            <Pagination
              page={pg.page}
              pageCount={pg.pageCount}
              total={pg.total}
              from={pg.from}
              to={pg.to}
              onPrev={pg.prev}
              onNext={pg.next}
            />
          ) : null}
        </Card>
      </div>
    </div>
  );
}
